import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { ratePlanEndpoints } from '../api/endpoints.js';
import { useSoftDeleteRatePlan } from '../api/mutations.js';
import { RATE_PLAN_STATUS_OPTIONS, type RatePlanRow, ratePlanRow } from '../api/schemas.js';

const filters: readonly FilterDescriptor[] = [
  {
    id: 'propertyId',
    label: 'Property',
    param: 'propertyId',
    kind: 'entity',
    optionsPath: '/v1/properties/options',
  },
  {
    id: 'status',
    label: 'Status',
    param: 'status',
    kind: 'select',
    options: RATE_PLAN_STATUS_OPTIONS,
  },
];

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const ratePlanColumns: readonly ColumnDescriptor<RatePlanRow>[] = [
  {
    id: 'name',
    header: 'Rate plan',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/rate-plans/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'currency',
    header: 'Currency',
    sortable: false,
    fields: ['currency'],
    cell: (row) => row.currency ?? '',
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'refundable',
    header: 'Refundable',
    sortable: false,
    fields: ['refundable'],
    cell: (row) => (row.refundable === true ? 'Yes' : 'No'),
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    fields: ['updatedAt'],
    cell: (row) => formatDate(row.updatedAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: RatePlanRow): React.JSX.Element => <RatePlanRowActions row={row} />,
  },
];

function RatePlanRowActions({ row }: { readonly row: RatePlanRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteRatePlan();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/rate-plans/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/rate-plans/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.PRICING_UPDATE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete rate plan"
        description={`${label} will be removed from this list. Existing reservations and rates that use it remain in place.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function RatePlanListPage(): React.JSX.Element {
  const { search } = useLocation();

  return (
    <ResourceListPage
      title="Rate plans"
      description="The rates a property sells, and the rules that govern them."
      path={ratePlanEndpoints.list}
      resource="ratePlans"
      schema={ratePlanRow}
      columns={ratePlanColumns}
      filters={filters}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.PRICING_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.PRICING_UPDATE}>
          <Button asChild>
            <Link to={{ pathname: '/rate-plans/new', search }}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              New rate plan
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
